/**
 * CompareBucketList — the "what this write will do automatically" stats block.
 *
 * Shared by the Compare modal (the preview) and the Add panel (the commit), so
 * the same run reads the same way on both. `testIdPrefix` defaults to
 * `"compare"`, which keeps every pre-existing `compare-*` testid byte-identical;
 * the panel passes `"export"`.
 *
 * The prefix exists because BOTH surfaces can be mounted at once — the export
 * overlay covering the Compare button is a layout accident, not an invariant —
 * and `ImportDetailPage.test.tsx` renders the whole page, where a duplicate
 * testid is a silent wrong-element match rather than a failure.
 */

import type { CompareBuckets } from "@nu-assist/contracts";
import type { ReactNode } from "react";
import { useMemo } from "react";

import { getBucketLabels } from "./bucketLabels";

export interface CompareBucketListProps {
  buckets: CompareBuckets | undefined;
  /** What the write is CALLED — heads the block ("{actionLabel} will automatically:"). */
  actionLabel: string;
  /** Company Map (BCT) copy variants — drops the "added to a search" language. */
  isBct?: boolean;
  /** Appended to the heading, e.g. `" (12 pending)"`. Empty by default. */
  headingSuffix?: string;
  /**
   * Replaces the whole `"{actionLabel} will automatically:"` phrase.
   *
   * The default is right in the Compare modal, which renders these buckets
   * immediately after producing them — there the run and the numbers are the
   * same population by construction. It is WRONG in the Add panel, where the
   * buckets come from the last stored compare and the run is whatever the
   * recruiter has selected now. Reported against the built app on 2026-09-16:
   * two contacts selected, and the block above the button read "Add Selected
   * to search will automatically: 11 new contacts… 75 in Thrive but not in
   * this search…" — 118 contacts' worth of promises for a two-contact run.
   *
   * An override rather than a mismatch check: `CompareResult` carries only
   * aggregates and the compare does not record which contacts it covered, so
   * "is this block about the selection?" cannot be answered here. Two earlier
   * iterations tried to detect the mismatch and both mis-modelled the
   * exclusion lists.
   */
  heading?: string;
  testIdPrefix?: string;
  /**
   * Extra `<li>`s appended INSIDE the list, after the four buckets.
   *
   * The modal closes its reconciliation with an ignored-contacts line, and that
   * line has to sit inside the same `<ul>` or it reads as a separate statement
   * rather than as the row that makes the buckets sum to the scope. A slot
   * keeps it where it was without this component having to know what an
   * "ignored" contact is — the panel passes nothing.
   */
  children?: ReactNode;
}

export function CompareBucketList({
  buckets,
  actionLabel,
  isBct = false,
  headingSuffix = "",
  heading,
  testIdPrefix = "compare",
  children,
}: CompareBucketListProps) {
  // Memoized and camelCase, deliberately. It was `const BUCKET_LABELS =
  // getBucketLabels(isBct)` in the render body: ALL_CAPS advertises a module
  // constant, but this one is rebuilt on every render — four closures per
  // paint for a value whose only input cannot change while mounted
  // (/pr-review iter 1, A 2b + 3c).
  const bucketLabels = useMemo(() => getBucketLabels(isBct), [isBct]);
  return (
    <section className="mt-5" data-testid={`${testIdPrefix}-stats`}>
      {/* Reads the page's `addAction` through `actionLabel`, so it
          cannot drift from the CTA the recruiter just clicked. #743
          retired "Export to Thrive" as the user-facing verb because
          "export" tells a recruiter nothing; this heading was the
          last place in the modal still deciding that for itself. */}
      <h3 className="text-h3 text-text">
        {heading ?? `${actionLabel} will automatically:`}
        {headingSuffix}
      </h3>
      <ul className="mt-2 space-y-1.5">
        {bucketLabels.map(({ key, label }) => (
          <li
            key={key}
            data-testid={`${testIdPrefix}-bucket-${key}`}
            className="text-body text-text-secondary"
          >
            {label(buckets?.[key] ?? 0)}
          </li>
        ))}
        {children}
      </ul>
    </section>
  );
}
